import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { Image as ImageIcon } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useChatAppContextInputs } from '@/chatContext/chatAppContextStore';
import {
  chatAnchorRefContextKey,
  chatContextKey,
} from '@/chatContext/chatContextKey';
import {
  selectActiveConversationId,
  selectChatLimits,
  selectConversationMessageIds,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatStreamDeps } from '@/services/chatStream/chatStreamClient';
import type { ChatBlockPlacement, ChatCitation } from '@/types/chatStream';
import type { ChatAppContextEnvelope, ChatSurface } from '@/types/chatEnvelope';
import ChatBlockContinueOffer from './ChatBlockContinueOffer';
import ChatComposer, { type ChatComposerAttachmentInput } from './ChatComposer';
import ChatContextPills, {
  useChatContextPlaceholder,
} from './ChatContextPills';
import { CHAT_PAGE_PLACEHOLDER } from './useCyclingPlaceholder';
import ChatSuggestedPrompts, {
  ChatSuggestedPromptsGreeting,
} from './ChatSuggestedPrompts';
import ChatThread, { ChatThreadLoading } from './ChatThread';
import { useChatBlockRenderer } from './blocks/useChatBlockRenderer';
import type { ChatQuestionFollowup } from './blocks/chatQuestionFollowup';
import {
  resolveChatCitationTarget,
  resolveChatDocumentCitation,
  resolveLectureCitation,
} from './blocks/chatBlockTargets';
import { useChatSend } from './useChatSend';
import {
  CompanionAnalyticsSurfaceContext,
  companionSurfaceForEnvelope,
  type CompanionAnalyticsSurface,
} from './companionAnalytics';
import { usePendingChatSend } from './usePendingChatSend';
import useVisualViewportInset from './useVisualViewportInset';
import { cn } from '@/lib/utils';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import { useIsMcGrawHill } from '../../../../mcgrawHill/mcgrawHill'; // mcgraw-hill
import { MCGRAW_HILL_CHAT_PAGE_PLACEHOLDER } from '../../../../mcgrawHill/mcgrawHillChapter'; // mcgraw-hill
import McGrawHillHipaaBadge from '../../../../mcgrawHill/McGrawHillHipaaBadge'; // mcgraw-hill

// mcgraw-hill: lazy, for a chapter's chat.
const LectureStudyMenu = lazy(() => import('../../Documents/LectureStudyMenu'));

export interface ChatConversationViewProps {
  /**
   * Which conversation to show. Omit it to follow whichever conversation the
   * slice has active — that is how the drawer and the page stay on the same
   * thread. Pass `null` to start a new one.
   */
  conversationId?: string | null;
  /**
   * This surface's app-context envelope (ORA-449). The shells supply it from
   * `useAppContextEnvelope`; a caller that omits it falls back to whatever the
   * visible surface published.
   */
  getEnvelope?: () => ChatAppContextEnvelope | undefined;
  /**
   * Which shell this view is inside. Served content reads it — a video plays
   * inline on the page and stays a card in the narrow drawer.
   */
  surface?: ChatSurface;
  /**
   * Called when a citation or confirmed action takes the student out of the
   * conversation. The drawer closes on it so it does not cover what they asked
   * to see; the page has nothing to close.
   */
  onNavigateOut?: () => void;
  /** See `ChatThread`'s `scrollHandoffKey`. */
  threadScrollHandoffKey?: string;
  /** Re-read this conversation, for example after a presigned image expires. */
  onReloadConversation?: (conversationId: string) => void | Promise<void>;
  /**
   * Override the block registry. Defaults to it, so served content renders
   * wherever a conversation does.
   */
  renderBlocks?: (
    blocks: ChatBlock[],
    placement: ChatBlockPlacement,
    onQuestionAnswered?: (question: ChatQuestionFollowup) => void,
    onBeforeContentExpand?: () => void
  ) => ReactNode;
  /** Dev/test transport override; production leaves it undefined. */
  streamDeps?: ChatStreamDeps;
  /**
   * Draw each turn's tool calls and result summaries under its status line,
   * and keep them after the turn. The dev harness (`/dev/chat`) passes it;
   * the student surfaces never do (ORA-514).
   */
  showToolActivity?: boolean;
  emptyState?: ReactNode;
  /**
   * Rendered above the greeting on an empty chat page (the AI desktop puts
   * the student's avatar there). Ignored when `emptyState` replaces the
   * page's own empty state.
   */
  emptyStateLead?: ReactNode;
  /** Overrides the chip layout the surface would otherwise pick. */
  promptLayout?: 'wrap' | 'stack' | 'columns';
  /**
   * Offer the preset prompt chips on an empty conversation (ORA-509). They
   * step aside for an explicit `emptyState`, an initial input the surface
   * opened with, and once the daily cap is spent — a chip that cannot send
   * should not look like it can.
   */
  showSuggestedPrompts?: boolean;
  placeholder?: string;
  /** The anchor changed and its prior thread is still being resolved. */
  isThreadResolving?: boolean;
  /** The ORA-505 lookup has resolved the conversation for this exact anchor. */
  isThreadReady?: boolean;
  /** A mounted-but-hidden shell must not claim an external send request. */
  isPendingSendEnabled?: boolean;
  /** The current New Chat action must create, rather than resume, its thread. */
  forceNewConversation?: boolean;
  /** Request composer focus when a visible surface has just opened. */
  autoFocusComposer?: boolean;
  /**
   * The shell the companion's events report (ORA-1118). The drawer shell says
   * which of its three chromes this is; otherwise it follows `surface`.
   */
  analyticsSurface?: CompanionAnalyticsSurface;
}

/**
 * Thread plus composer over one conversation — everything the student
 * interacts with, and the only place the two shells have in common.
 *
 * It holds no conversation state of its own: messages, stream status, and
 * limits all come from the chat slice, so the drawer and the page render the
 * same conversation without handing anything to each other.
 */
const ChatConversationView = ({
  conversationId,
  getEnvelope,
  surface = 'chat_page',
  onNavigateOut,
  threadScrollHandoffKey,
  onReloadConversation,
  renderBlocks,
  streamDeps,
  showToolActivity,
  emptyState,
  emptyStateLead,
  promptLayout: promptLayoutOverride,
  showSuggestedPrompts = false,
  placeholder,
  isThreadResolving = false,
  isThreadReady = true,
  isPendingSendEnabled = true,
  forceNewConversation = false,
  autoFocusComposer = false,
  analyticsSurface: analyticsSurfaceProp,
}: ChatConversationViewProps) => {
  const analyticsSurface =
    analyticsSurfaceProp ?? companionSurfaceForEnvelope(surface);

  const activeConversationId = useSelector(selectActiveConversationId);
  const limits = useSelector(selectChatLimits);
  const isMcGrawHill = useIsMcGrawHill();
  const navigate = useNavigate();
  const { open: openSideViewer } = useChatDocumentViewer();
  const composerAttachmentInput = useRef<ChatComposerAttachmentInput>(null);
  const dragDepth = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  // The composer's box is not empty (ORA-606). Only the empty/non-empty flip
  // reaches here, so the thread does not re-render per keystroke.
  const [isDrafting, setIsDrafting] = useState(false);

  // "Ask Ora about this" publishes the highlighted text as the app-context
  // prefill (ORA-449); the composer opens on it so the student sees what they
  // are about to ask. A selection or prefill means the next ask is already
  // staged, so suggestions step aside until that input is sent or cleared.
  const { anchor, selection, prefill, detachedAnchorKey } =
    useChatAppContextInputs();

  const renderRegistryBlocks = useChatBlockRenderer({ surface });
  // On a phone the on-screen keyboard covers the bottom of the window, and
  // the bottom of this column is the composer (ORA-599). Lift the whole
  // column by however much it is covering.
  const keyboardInset = useVisualViewportInset();

  const requestedConversationId =
    conversationId === undefined ? activeConversationId : conversationId;

  // The hook resolves a `null` start onto the conversation its first turn
  // created, so a new thread stops being empty the moment it exists.
  const {
    conversationId: shownConversationId,
    send,
    sendIfAccepted,
    retry,
    stop,
    isStreaming,
    isBusy,
    isLimitReached,
    sendRejection,
  } = useChatSend({
    conversationId: requestedConversationId,
    getEnvelope,
    streamDeps,
    isThreadResolving,
    forceNewConversation,
    analyticsSurface,
  });

  const isThreadEmpty = useSelector(
    (state: ChatRootState) =>
      selectConversationMessageIds(state, shownConversationId).length === 0
  );

  const isEmptyPage =
    surface === 'chat_page' && isThreadEmpty && !isThreadResolving;

  /**
   * Nothing to show yet: the anchor's thread is still being looked up, and
   * until it lands the slice has no active conversation. Both surfaces would
   * otherwise render their empty state and flash the real thread into it a
   * second later (ORA-696). A conversation that already has messages keeps
   * rendering them.
   */
  const isThreadLoading = isThreadResolving && isThreadEmpty;

  usePendingChatSend({
    target: surface === 'chat_page' || surface === 'drawer' ? surface : null,
    enabled: isPendingSendEnabled,
    isThreadReady,
    isBusy,
    isLimitReached,
    send,
  });

  /**
   * A lecture or attachment page opens in the side viewer beside this
   * conversation (ORA-736), or scrolls it when that document is already open.
   * Everything else with a destination — an article, a video, a help doc, the
   * student's own performance (ORA-605) — is pushed through the router rather
   * than reloading the app. A citation with nowhere honest to go resolves to
   * nothing and stays inert.
   */
  const selectCitation = useCallback(
    (citation: ChatCitation) => {
      const target = resolveChatCitationTarget(citation);
      if (!target) return;

      const lecturePage = resolveLectureCitation(citation);

      if (lecturePage) {
        const { lectureId, page, factIds, regions } = lecturePage;

        openSideViewer(
          { kind: 'lecture', lectureId },
          page || regions
            ? {
                ...(page ? { page } : {}),
                ...(factIds ? { factIds } : {}),
                ...(regions ? { regions } : {}),
              }
            : undefined
        );

        return;
      }

      const chatDocumentPage = resolveChatDocumentCitation(citation);

      if (chatDocumentPage) {
        openSideViewer(
          { kind: 'chatDocument', documentId: chatDocumentPage.documentId },
          {
            page: chatDocumentPage.page,
            // mcgraw-hill: highlight the passage used.
            ...(citation.highlightExcerpt && citation.excerpt
              ? { highlightText: citation.excerpt }
              : {}),
          }
        );

        return;
      }

      navigate(target);
      onNavigateOut?.();
    },
    [navigate, onNavigateOut, openSideViewer]
  );

  /**
   * A follow-up chip asks its prompt exactly as if the student had typed it:
   * the same `send`, the same guards, the same envelope. Withheld once the
   * daily allowance is spent, so a chip never looks like it can send.
   *
   * The chip's own anchor replaces the surface's, so the detach flag has to be
   * re-decided against it (ORA-662): a chip about the very item the student
   * removed stays detached, and a chip about anything else — a question block
   * Ora served into the thread — is hydrated normally.
   */
  const sendFollowup = useCallback(
    (prompt: string, anchor?: ChatQuestionFollowup['anchor']) => {
      const envelope = getEnvelope?.();

      if (!anchor) {
        void send({ message: prompt, entry: 'followup_chip' });
        return;
      }

      const base = envelope ?? {
        surface,
        route: 'chat' as const,
        activity: { kind: 'idle' as const },
      };

      const isDetached =
        detachedAnchorKey !== null &&
        detachedAnchorKey === chatAnchorRefContextKey(anchor);

      const followupEnvelope: ChatAppContextEnvelope = { ...base, anchor };

      // The surface's flag must not ride along onto another item's anchor.
      if (isDetached) followupEnvelope.anchorDetached = true;
      else delete followupEnvelope.anchorDetached;

      void send({
        message: prompt,
        envelope: followupEnvelope,
        entry: 'followup_chip',
      });
    },
    [detachedAnchorKey, getEnvelope, send, surface]
  );

  const hasDraggedFiles = (event: DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types).includes('Files');

  const canAcceptDroppedFiles = () =>
    !Capacitor.isNativePlatform() &&
    !isStreaming &&
    !isThreadResolving &&
    !isLimitReached &&
    !composerAttachmentInput.current?.isAtAttachmentLimit();

  const resetDraggedFiles = useCallback(() => {
    dragDepth.current = 0;
    setIsDraggingFiles(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') resetDraggedFiles();
    };

    window.addEventListener('dragend', resetDraggedFiles);
    window.addEventListener('blur', resetDraggedFiles);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('dragend', resetDraggedFiles);
      window.removeEventListener('blur', resetDraggedFiles);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [resetDraggedFiles]);

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (Capacitor.isNativePlatform() || !hasDraggedFiles(event)) return;

    event.preventDefault();
    if (!canAcceptDroppedFiles()) return;

    dragDepth.current += 1;
    setIsDraggingFiles(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (Capacitor.isNativePlatform() || !hasDraggedFiles(event)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = canAcceptDroppedFiles() ? 'copy' : 'none';
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (Capacitor.isNativePlatform() || dragDepth.current === 0) return;

    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDraggingFiles(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (Capacitor.isNativePlatform()) return;

    if (hasDraggedFiles(event) || dragDepth.current > 0) event.preventDefault();

    resetDraggedFiles();

    if (!canAcceptDroppedFiles()) {
      composerAttachmentInput.current?.showAttachmentRefusal();
      return;
    }

    const fileItems = Array.from(event.dataTransfer.items).filter(
      (item) => item.kind === 'file'
    );

    const files =
      fileItems.length > 0
        ? fileItems.flatMap((item) => {
            if (item.webkitGetAsEntry?.()?.isDirectory) return [];

            const file = item.getAsFile();
            return file ? [file] : [];
          })
        : Array.from(event.dataTransfer.files);

    if (files.length === 0) return;

    composerAttachmentInput.current?.addFiles(files, 'drop');
  };

  const shouldShowSuggestedPrompts =
    showSuggestedPrompts &&
    emptyState === undefined &&
    !isLimitReached &&
    !selection &&
    !prefill;

  // Both shells already resolved this route in their envelope hook; reuse it.
  const promptEnvelope = getEnvelope?.();
  const promptRoute = promptEnvelope?.route ?? 'other';

  // Only the full chat page is wide enough for a row of chips to wrap
  // sensibly; the drawer and the document split pane stack them (ORA-628).
  const promptLayout =
    promptLayoutOverride ??
    (promptEnvelope?.surface === 'chat_page' ? 'wrap' : 'stack');

  const suggestedPrompts = shouldShowSuggestedPrompts ? (
    <ChatSuggestedPrompts
      route={promptRoute}
      anchor={anchor}
      layout={promptLayout}
      onSend={(prompt) => {
        void send({ message: prompt, entry: 'prompt_chip' });
      }}
    />
  ) : undefined;

  const emptyPageContent =
    emptyState ??
    (shouldShowSuggestedPrompts ? (
      <ChatSuggestedPrompts
        showGreeting={false}
        route={promptRoute}
        anchor={anchor}
        layout={promptLayout}
        onSend={(prompt) => {
          void send({ message: prompt, entry: 'prompt_chip' });
        }}
      />
    ) : null);

  // What Ora can see, shown before the send (ORA-662). Both production shells
  // already resolved the route and the surface in their envelope hook, so the
  // pills read them from there rather than matching the URL again.
  const pillSurface = promptEnvelope?.surface ?? surface;

  const contextPills = (
    <ChatContextPills route={promptRoute} surface={pillSurface} />
  );

  const contextPlaceholder = useChatContextPlaceholder({
    anchor,
    route: promptRoute,
    surface: pillSurface,
    isDetached: detachedAnchorKey === chatContextKey(anchor, promptRoute),
  });

  // It follows the pill; an explicit `placeholder` wins, and the composer's
  // blocked and out-of-chats copy beats both. An empty page with no pill cycles.
  const composerPlaceholder =
    placeholder ??
    contextPlaceholder ??
    (isEmptyPage
      ? // mcgraw-hill: its own endings.
        isMcGrawHill
        ? MCGRAW_HILL_CHAT_PAGE_PLACEHOLDER
        : CHAT_PAGE_PLACEHOLDER
      : undefined);

  // mcgraw-hill: a chapter's study pills.
  const mcgrawHillStudyPills =
    isMcGrawHill && anchor?.type === 'lecture' ? (
      <Suspense fallback={null}>
        <LectureStudyMenu lectureId={anchor.lectureId} variant="pills" />
      </Suspense>
    ) : null;

  const composer = (
    <ChatComposer
      attachmentInputRef={composerAttachmentInput}
      contextPills={contextPills}
      onSend={(message, attachments, documentIds, documentAttachments) =>
        sendIfAccepted({
          message,
          attachments,
          documentIds,
          documentAttachments,
        })
      }
      onStop={stop}
      onDraftChange={setIsDrafting}
      isStreaming={isStreaming}
      isBlocked={isThreadResolving}
      limits={limits}
      placeholder={composerPlaceholder}
      prefill={prefill}
      autoFocus={autoFocusComposer}
      draftKey={shownConversationId ?? 'new'}
      sendRejection={sendRejection}
    />
  );

  return (
    <CompanionAnalyticsSurfaceContext.Provider value={analyticsSurface}>
      <div
        className={cn(
          'relative flex min-h-0 flex-1 flex-col',
          isEmptyPage && 'companion-chat-page-empty overflow-y-auto'
        )}
        // The ancestors already pad for the home indicator
        // (`_capacitor.scss`), and that padding
        // stays put while the keyboard is up — so subtract it once rather than
        // stack a safe area on top of a keyboard that is already covering it.
        style={
          keyboardInset > 0
            ? {
                paddingBottom: `max(0px, calc(${keyboardInset}px - env(safe-area-inset-bottom)))`,
              }
            : undefined
        }
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDraggingFiles ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border-subtle bg-surface-canvas/90 font-heading text-sm font-medium text-fg-primary"
          >
            <ImageIcon size={28} aria-hidden="true" />
            <span>Drop files to attach</span>
          </div>
        ) : null}
        {isThreadLoading ? (
          <ChatThreadLoading />
        ) : isEmptyPage ? (
          // The greeting is the page's heading, not part of the chip group: a
          // staged prefill or a reached daily limit hides the chips, and the page
          // must not go blank above the composer.
          emptyState === undefined ? (
            <div className="companion-chat-page-empty__lead mt-auto flex w-full shrink-0 flex-col items-center px-3 mobile:px-4">
              {emptyStateLead}
              <ChatSuggestedPromptsGreeting
                headingLevel="h1"
                centered
                headingClassName="m-0! text-2xl tracking-tight mobile:text-3xl"
              />
            </div>
          ) : null
        ) : (
          <ChatThread
            conversationId={shownConversationId}
            // Retrying spends a chat, so it is offered on the same terms as the
            // composer: not while a turn runs, and not once the cap is reached.
            onRetry={isStreaming || isLimitReached ? undefined : retry}
            renderBlocks={renderBlocks ?? renderRegistryBlocks}
            emptyState={emptyState ?? suggestedPrompts}
            onSelectCitation={selectCitation}
            onReloadImages={
              shownConversationId && onReloadConversation
                ? () => void onReloadConversation(shownConversationId)
                : undefined
            }
            onSendFollowup={isLimitReached ? undefined : sendFollowup}
            isDrafting={isDrafting}
            showToolActivity={showToolActivity}
            onActionNavigate={onNavigateOut}
            scrollHandoffKey={threadScrollHandoffKey}
          />
        )}
        {/* On the empty page the composer and the suggestion columns share one
          block so the two line up exactly: the block is as wide as the
          columns' own text (`lg:w-max` on the prompts) and the composer fills
          it. The wrapper is always rendered, so the composer keeps its DOM
          node — and with it the draft and the focus — when the first turn
          turns this page into a thread. */}
        <div
          className={cn(
            'flex shrink-0 flex-col',
            isEmptyPage &&
              (isMcGrawHill
                ? // mcgraw-hill: no columns to size it; the usual width.
                  'mb-auto w-full max-w-208 self-center'
                : 'mb-auto w-full max-w-3xl self-center md:max-w-none lg:w-max lg:max-w-full')
          )}
        >
          <div className="shrink-0 px-3 mobile:px-4">
            {mcgrawHillStudyPills}
            <ChatBlockContinueOffer
              conversationId={shownConversationId}
              surface={surface}
            />
            {composer}
          </div>
          {isEmptyPage ? (
            <div className="shrink-0 px-3 mobile:px-4">{emptyPageContent}</div>
          ) : null}
          {isEmptyPage && isMcGrawHill && !anchor && <McGrawHillHipaaBadge />}
        </div>
      </div>
    </CompanionAnalyticsSurfaceContext.Provider>
  );
};

export default ChatConversationView;
